import React, { useCallback, useMemo } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Client } from "@/lib/types/database";
import { QuickContactCreationModal } from "@/features/contacts/components/QuickContactCreationModal";
import { useCreateContact } from "@/features/contacts/hooks";
import { useModalManager } from "./hooks/useModalManager";
import type { SelectOption } from "@/components/ui/responsive-select";
import { ContactOptionPreview } from "./ContactOptionPreview";
import { EntitySelectShell } from "./shared/EntitySelectShell";
import {
  ADD_NEW_CONTACT_VALUE,
  buildContactOptions,
  placeholderEmailFromName,
  shouldOfferInlineCreate,
  type EnhancedSelectOption,
} from "./client-select/contact-options";
import { useClientSelectContacts } from "./client-select/useClientSelectContacts";

interface ClientSelectProps {
  value: string;
  onValueChange: (value: string, option?: SelectOption) => void;
  items?: Client[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  isSearching?: boolean;
  onSearchChange?: (query: string) => void;
  searchTerm?: string;
  fullWidth?: boolean;
  /** When true, expand inline (search + list below) instead of popover. Default true for tables/modals. Pass false for popover. */
  expandInline?: boolean;
  /** When expandInline: "floating" (default) = trigger stays, dropdown floats; "replace" = trigger replaced by panel */
  expandInlineVariant?: "replace" | "floating";
  /** Use a higher z-index — needed when rendering inside another modal/drawer. */
  highZIndex?: boolean;
  [key: string]: unknown;
}

interface ContactCreatedData {
  contactId: string;
  contactData?: Record<string, unknown>;
}

const renderContactOption = (option: SelectOption): React.ReactNode => {
  const o = option as EnhancedSelectOption;
  return (
    <ContactOptionPreview
      label={o.label}
      status={o.status}
      companyName={o.companyName}
      title={o.title}
      email={o.email}
      phone={o.phone}
    />
  );
};

export const ClientSelectWithModal = React.memo<ClientSelectProps>((props) => {
  const {
    value,
    onValueChange,
    items,
    placeholder,
    disabled,
    isSearching,
    onSearchChange,
    expandInline = true,
    expandInlineVariant = "floating",
    highZIndex,
  } = props;

  const {
    searchValue,
    setSearchValue,
    searchedClients,
    isFetching,
    isBackgroundFetching,
    page,
    totalPages,
    firstPage,
    hasSearchTerm,
    loadMore,
  } = useClientSelectContacts();

  const {
    showModal: showCreateModal,
    handleAddNew,
    handleItemCreated: handleContactCreated,
    handleModalClose,
  } = useModalManager<ContactCreatedData>();

  const handleContactCreatedWrapper = useCallback(
    (contactId: string, contactData?: Record<string, unknown>) => {
      handleContactCreated({ contactId, contactData });
    },
    [handleContactCreated]
  );

  const baseOptions = useMemo(
    () =>
      buildContactOptions(
        searchedClients.length > 0 ? searchedClients : ((items as Client[] | undefined) ?? []),
        value,
        items as Client[] | undefined
      ),
    [searchedClients, items, value]
  );

  // Inline create: when the typed name matches no existing contact, offer an
  // "Add new contact named X" row that creates the contact with a placeholder
  // email (the user fills the real one in later).
  const createContact = useCreateContact();
  const trimmedSearch = searchValue.trim();
  const showAddNew = useMemo(
    () => shouldOfferInlineCreate(trimmedSearch, baseOptions, createContact.isPending),
    [trimmedSearch, baseOptions, createContact.isPending]
  );

  const options = useMemo<SelectOption[]>(() => {
    if (!showAddNew) return baseOptions;
    const addNewOption: SelectOption = {
      value: ADD_NEW_CONTACT_VALUE,
      label: `Add new contact "${trimmedSearch}"`,
      searchableText: trimmedSearch,
    };
    return [addNewOption, ...baseOptions];
  }, [showAddNew, baseOptions, trimmedSearch]);

  const handleAddNewContact = useCallback(async () => {
    const name = trimmedSearch;
    if (!name) return;
    try {
      const created = await createContact.mutateAsync({
        name,
        email: placeholderEmailFromName(name),
        status: "lead",
      });
      if (created?.id) {
        onValueChange(String(created.id), {
          value: String(created.id),
          label: (created.name as string | undefined) ?? name,
        });
        setSearchValue("");
        toast.info(
          "Created with a placeholder email — open the contact to set their real email when you can."
        );
      }
    } catch {
      // useCreateContact already surfaces a toast on error.
    }
  }, [trimmedSearch, createContact, onValueChange, setSearchValue]);

  const renderOption = useCallback((option: SelectOption): React.ReactNode => {
    if (option.value === ADD_NEW_CONTACT_VALUE) {
      return (
        <div className="flex items-center gap-2 font-medium text-primary">
          <Plus className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{option.label}</span>
        </div>
      );
    }
    return renderContactOption(option);
  }, []);

  // A caller that tracks its own search (e.g. a form driving the query) owns the
  // spinner; otherwise fall back to whichever source is currently fetching.
  const isLoading = isSearching ?? isFetching;

  return (
    <>
      <EntitySelectShell
        value={value}
        onValueChange={onValueChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        onSearchChange={onSearchChange}
        isLoading={!!isLoading}
        recentKey="contacts"
        highZIndex={highZIndex}
        expandInline={expandInline}
        expandInlineVariant={expandInlineVariant}
        customOptionRenderer={renderOption}
        addNewSentinel={ADD_NEW_CONTACT_VALUE}
        onAddNew={handleAddNewContact}
        pageInfo={{
          page,
          totalPages,
          firstPage,
          isBackgroundFetching,
          onLoadMore: loadMore,
        }}
        disablePagination={hasSearchTerm}
        containerClassName="w-full"
        className="w-full"
      />
      <QuickContactCreationModal
        open={showCreateModal}
        onOpenChange={(open) => {
          if (open) {
            handleAddNew(({ contactId }: ContactCreatedData) => {
              onValueChange(contactId);
            });
          } else {
            handleModalClose();
          }
        }}
        onContactCreated={handleContactCreatedWrapper}
      />
    </>
  );
});

ClientSelectWithModal.displayName = "ClientSelectWithModal";
